import type {
    Campaign,
    CampaignCreate,
} from "@campaign/domain/models/campaign.model";
import { CropTypes } from "@campaign/domain/models/crop-type.model";
import { CampaignCreateUseCase } from "@campaign/domain/usecases/campaign-create.usecase";
import type { CampaignModalFormValuesValidated } from "@campaign/presentation/components/campaign/campaign-modal-form.view";
import { ToastManagerRepository } from "@core/domain/repositories/toast-manager.repository";
import type { Nullable } from "@core/domain/types/nullable.type";
import { LoadLayoutStore } from "@core/presentation/components/feedback/load-layout/load-layout.store";
import { BaseViewModel } from "@iot-platform/core";
import type { Station } from "@station/domain/models/station.model";
import { inject, injectable } from "inversify";
import { DateTime } from "luxon";
import { action, makeObservable, observable, runInAction } from "mobx";

@injectable()
export class CampaignCreateModalViewModel extends BaseViewModel {
    @observable
    initialLoading = true;

    @observable
    cropTypes: CropTypes = CropTypes.empty();

    @observable
    stations: Readonly<Station>[] = [];

    constructor(
        @inject(CampaignCreateUseCase)
        private readonly campaignCreateUseCase: CampaignCreateUseCase,
    ) {
        super();
        makeObservable(this);
    }

    override didMount(): void {
        this.initViewData();
    }

    @action
    initViewData(): void {
        runInAction(() => {
            this.initialLoading = false;
        });
    }

    @action
    async createCampaign(
        values: CampaignModalFormValuesValidated,
    ): Promise<Nullable<Campaign>> {
        LoadLayoutStore.start();

        const campaignData: CampaignCreate = {
            name: values.name,
            cropTypeId: values.cropTypeId,
            startDate: this.getDate(values.dateRange[0]),
            endDate: this.getDate(values.dateRange[1]),
            stationIds: values.stationIds,
        };

        const campaignCreated =
            await this.campaignCreateUseCase.execute(campaignData);

        if (campaignCreated) {
            ToastManagerRepository.success();
        }

        LoadLayoutStore.finish();

        return campaignCreated;
    }

    private getDate(date: DateTime | undefined): string {
        return date ? date.toFormat("dd/MM/yyyy") : "";
    }
}
